// PNG scanline filters and big-endian sample assembly are defined by the format in terms of byte
// arithmetic; spelling them out with Math instead would obscure the spec rather than clarify it.
/* eslint-disable no-bitwise */

// Pure 16-bit greyscale PNG decoding. Deliberately free of imports: `inflate` is injected so the
// decoder can be exercised by a plain Node test with zlib, without pulling in pako or a DOM.
// The parts that actually go wrong here -- scanline filters and big-endian sample assembly --
// are then covered by a test that compares against the same file read through tifffile.

export interface Gray16 {
    width: number;
    height: number;
    data: Uint16Array;
    max: number;
}

export type Inflate = (input: Uint8Array) => Uint8Array;

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

// Undo the per-scanline PNG filters. bpp is 2 for 16-bit greyscale, so "the byte to the left" is
// two bytes back; getting that wrong produces a diagonal smear rather than a clean error, which
// is why it is spelled out longhand rather than folded into a clever loop.
function unfilter(raw: Uint8Array, width: number, height: number): Uint8Array {
    const bpp = 2;
    const stride = width * bpp;
    const out = new Uint8Array(stride * height);
    let pos = 0;
    for (let y = 0; y < height; y++) {
        const filter = raw[pos];
        pos += 1;
        const row = y * stride;
        const prev = row - stride;
        for (let x = 0; x < stride; x++) {
            const value = raw[pos + x];
            const a = x >= bpp ? out[row + x - bpp] : 0;
            const b = y > 0 ? out[prev + x] : 0;
            const c = (x >= bpp && y > 0) ? out[prev + x - bpp] : 0;
            let result: number;
            if (filter === 0) result = value;
            else if (filter === 1) result = value + a;
            else if (filter === 2) result = value + b;
            else if (filter === 3) result = value + ((a + b) >> 1);
            else if (filter === 4) {
                // Paeth: pick whichever neighbour the linear predictor lands closest to.
                const p = a + b - c;
                const pa = Math.abs(p - a);
                const pb = Math.abs(p - b);
                const pc = Math.abs(p - c);
                let pred = c;
                if (pa <= pb && pa <= pc) pred = a;
                else if (pb <= pc) pred = b;
                result = value + pred;
            } else throw new Error(`unsupported PNG filter ${filter}`);
            out[row + x] = result & 0xff;
        }
        pos += stride;
    }
    return out;
}

/** Returns null for anything that is not a non-interlaced 16-bit greyscale PNG. */
export function decodeGray16PNG(buffer: ArrayBuffer, inflate: Inflate): Gray16 | null {
    const bytes = new Uint8Array(buffer);
    if (bytes.length < 8) return null;
    for (let i = 0; i < 8; i++) {
        if (bytes[i] !== PNG_SIGNATURE[i]) return null;
    }
    const view = new DataView(buffer);
    let offset = 8;
    let width = 0;
    let height = 0;
    const idat: Uint8Array[] = [];
    while (offset + 8 <= bytes.length) {
        const length = view.getUint32(offset);
        const type = String.fromCharCode(
            bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7],
        );
        const body = offset + 8;
        if (type === 'IHDR') {
            width = view.getUint32(body);
            height = view.getUint32(body + 4);
            const bitDepth = bytes[body + 8];
            const colorType = bytes[body + 9];
            const interlace = bytes[body + 12];
            if (bitDepth !== 16 || colorType !== 0 || interlace !== 0) return null;
        } else if (type === 'IDAT') {
            idat.push(bytes.subarray(body, body + length));
        } else if (type === 'IEND') {
            break;
        }
        offset = body + length + 4;
    }
    if (!width || !height || !idat.length) return null;

    let total = 0;
    idat.forEach((part) => { total += part.length; });
    const joined = new Uint8Array(total);
    let at = 0;
    idat.forEach((part) => { joined.set(part, at); at += part.length; });

    // A short stream -- a truncated response, a partial write on the server -- would otherwise
    // read past the end of the buffer inside unfilter, where `undefined` arithmetic turns into
    // NaN and lands as 0. That is a silently corrupt image, which is worse than no image: the
    // annotator would be drawing on garbage without a hint that anything went wrong.
    const inflated = inflate(joined);
    if (inflated.length < (width * 2 + 1) * height) return null;

    const raw = unfilter(inflated, width, height);
    const data = new Uint16Array(width * height);
    let max = 0;
    for (let i = 0; i < data.length; i++) {
        const value = (raw[i * 2] << 8) | raw[i * 2 + 1]; // PNG samples are big-endian
        data[i] = value;
        if (value > max) max = value;
    }
    return {
        width, height, data, max,
    };
}
